import { useCallback } from 'react';

import { useClient } from 'urql';

import {
  ScanToPayPaymentTokenDocument,
  type ScanToPayPaymentTokenQuery,
} from '../../graphql/ScanToPayPaymentToken.generated';
import { type ScanToPayMachineContext } from '../../machine/scan-to-pay.machine.types';
import { getDefaultPaymentMethodId } from '../../utils';

// ────────────────────────────────────────────────────────────────────────────

/**
 * A hook for fetching the payment token.
 */
export const useScanToPayPaymentToken = () => {
  const client = useClient();

  // ─── Queries ──────────────────────────────────────────────────────────────

  const fetchPaymentToken = useCallback(
    async (context: ScanToPayMachineContext) => {
      const { paymentMethodId, paymentMethods, useCredit } = context;
      const defaultPaymentMethodId = getDefaultPaymentMethodId(paymentMethods);

      const queryPaymentToken = client.query<ScanToPayPaymentTokenQuery>;

      const response = await queryPaymentToken(
        ScanToPayPaymentTokenDocument,
        {
          paymentMethodId: paymentMethodId ?? defaultPaymentMethodId,
          useCredit,
        },
        { requestPolicy: 'network-only' },
      ).toPromise();

      const customerToken = response.data?.loyaltyProfile?.customerToken;

      if (customerToken?.__typename === 'LoyaltyAccessToken') {
        return { paymentToken: customerToken.token };
      }

      // Caught by the machine.
      throw new Error('Failed to fetch payment token');
    },
    [client.query],
  );

  // ──────────────────────────────────────────────────────────────────────────

  return { fetchPaymentToken };
};
